import { PERMISSION_COVERS, USER_ROLES } from "./permissions.constants";

import { useUserStore } from "@/shared/stores/useUserStore";

// ─────────────────────────────────────────────────────────────────────────────
// Pure permission-check functions — no Vue reactivity, no composable context.
// Safe to call from router guards, store actions, and plain utility code.
//
// In Vue components or feature composables use usePermissions() instead,
// which wraps these with proper composable ergonomics.
//
// All functions accept an optional `userStore` parameter so they can be called
// outside a component setup context (e.g., in router beforeEach) without
// triggering Pinia's "no active Pinia" error.
// ─────────────────────────────────────────────────────────────────────────────

type UserStore = ReturnType<typeof useUserStore>;

// ── ADMIN bypass ──────────────────────────────────────────────────────────────
// ADMIN role has universal access and skips all permission checks.
// If additional unrestricted roles are introduced, extend this function.
const isUnrestrictedRole = (store: UserStore): boolean =>
  store.user?.role === USER_ROLES.ADMIN;

// A grant holds every permission it covers (catalog.admin → catalog.import), as on the backend.
const holdsPermission = (store: UserStore, required: string): boolean =>
  store.currentOrgPermissions.some((granted) => {
    const implied: readonly string[] = PERMISSION_COVERS[granted] ?? [granted];
    return implied.includes(required);
  });

// ── hasPermissions — AND logic ────────────────────────────────────────────────
// Returns true if the user holds ALL of the required permissions.
// Used by the route guard (meta.permissions) and for multi-permission actions.
//
// Single-permission check: hasPermissions([PERMISSIONS.CATALOG_READ])
// Multi-permission check:  hasPermissions([PERMISSIONS.CATALOG_READ, PERMISSIONS.CATALOG_WRITE])
export const hasPermissions = (
  required: readonly string[] | undefined,
  userStore?: UserStore,
): boolean => {
  const store = userStore ?? useUserStore();

  // Empty requirement → open to any authenticated user (no permission gate)
  if (!required || required.length === 0) return true;

  if (!store.isAuthenticated || !store.user) return false;

  // ADMIN bypasses all permission checks
  if (isUnrestrictedRole(store)) return true;

  return required.every(p => holdsPermission(store, p));
};

// ── hasAnyPermission — OR logic ───────────────────────────────────────────────
// Returns true if the user holds AT LEAST ONE of the required permissions.
// Use when any level of access is sufficient (read OR write OR admin).
export const hasAnyPermission = (
  required: readonly string[] | undefined,
  userStore?: UserStore,
): boolean => {
  const store = userStore ?? useUserStore();

  if (!required || required.length === 0) return true;

  if (!store.isAuthenticated || !store.user) return false;

  if (isUnrestrictedRole(store)) return true;

  return required.some(p => holdsPermission(store, p));
};

// ── hasRoles ──────────────────────────────────────────────────────────────────
// Returns true if the user's role is included in the required list (OR logic).
// Used for role-gated routes (e.g., /organizations → ADMIN only).
//
// Note: ADMIN bypass does NOT apply here by design.
// roles: ["ADMIN"] means strictly ADMIN users only — permission checks don't override this.
// If a user is ADMIN but the route also has roles: ["SUPER_ADMIN"], ADMIN won't pass.
export const hasRoles = (
  required: readonly string[] | undefined,
  userStore?: UserStore,
): boolean => {
  const store = userStore ?? useUserStore();

  // No role requirement → any authenticated user passes
  if (!required || required.length === 0) return true;

  if (!store.isAuthenticated || !store.user) return false;

  return required.includes(store.user.role);
};
